import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Mail } from 'lucide-react';
import { NavLink } from './navLinks';
import { MobileAccordionMenu } from './MobileAccordionMenu';
import { getMenuConfigByLabel, menuConfigs, MenuSectionConfig, appendProspectus } from './menuConfig';
import { useProfessionalMenuItems } from '@/hooks/navbar/useProfessionalMenuItems';
import { usePrivateMenuItems } from '@/hooks/navbar/usePrivateMenuItems';

interface MobileNavigationProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  filteredNavLinks: NavLink[];
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  filteredNavLinks
}) => {
  // Récupérer les items depuis la DB
  const { data: professionalItems } = useProfessionalMenuItems();
  const { data: privateItems } = usePrivateMenuItems();

  // Créer une config dynamique qui merge les données DB avec la config statique
  const getDynamicMenuConfig = useMemo(() => {
    return (label: string): MenuSectionConfig | null => {
      let base: MenuSectionConfig | null;
      if (label.toLowerCase() === 'particuliers' && privateItems && privateItems.length > 0) {
        base = { ...menuConfigs.particuliers, items: privateItems };
      } else if (label.toLowerCase() === 'professionnels' && professionalItems && professionalItems.length > 0) {
        base = { ...menuConfigs.professionnels, items: professionalItems };
      } else {
        base = getMenuConfigByLabel(label);
      }
      // Lien "Télécharger un prospectus" ajouté sous les évènements (par audience)
      return appendProspectus(base, label);
    };
  }, [professionalItems, privateItems]);

  return (
    <>
      <Button 
        variant="ghost" 
        size="icon" 
        className="lg:hidden" 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t lg:hidden z-50 max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="px-4 py-2">
            {filteredNavLinks.map(link => {
              const menuConfig = getDynamicMenuConfig(link.label);
              
              // Use accordion menu for items with config
              if (menuConfig) {
                return (
                  <MobileAccordionMenu 
                    key={link.path}
                    config={menuConfig}
                    onLinkClick={() => setMobileMenuOpen(false)}
                  />
                );
              }
              
              // Simple link for items without submenu
              return (
                <Link 
                  key={link.path} 
                  to={link.path} 
                  className="flex items-center gap-2 w-full text-left py-3 px-2 text-foreground font-medium hover:text-accent transition-colors border-b border-border last:border-0" 
                  onClick={(e) => {
                    if (link.onClick) {
                      link.onClick(e);
                    }
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
            
            {/* Actions buttons in mobile menu */}
            <div className="flex flex-col gap-3 pt-4 border-t border-border mt-4">
              {/* Évènements privés */}
              <Link 
                to="/univers-particuliers" 
                className="bg-primary text-white hover:bg-primary/90 text-center py-4 px-4 rounded-lg font-medium transition-colors flex items-center justify-center" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">Évènement privé</span>
              </Link>
              
              {/* Évènements professionnels */}
              <Link 
                to="/evenement-entreprise-prestige" 
                className="bg-accent text-white hover:bg-accent/90 text-center py-4 px-4 rounded-lg font-medium transition-colors flex items-center justify-center" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">Évènement professionnel</span>
              </Link>
              
            </div>
          </nav>
        </div>
      )}
    </>
  );
};
